import AuthService from "../../services/AuthService";
import * as action from './actionCreators';
import * as type from './types';
import {
  bumpAuthSession,
  clearPersistedUserState,
  getAuthSession,
  getUserIdFromToken,
  isCurrentAuthSession,
  profileMatchesLoginEmail,
} from '../../authSession';

let backgroundProfileAbortController = new AbortController();

const abortBackgroundProfileFetch = () => {
  backgroundProfileAbortController.abort();
  backgroundProfileAbortController = new AbortController();
};

const isRequestCancelled = (error) =>
  error?.code === "ERR_CANCELED" || error?.name === "CanceledError";

export const loginUser = (credentials) => async (dispatch) => {
  const sessionId = bumpAuthSession();
  const loginEmail = credentials.email.trim().toLowerCase();
  const loginAbortController = new AbortController();

  try {
    abortBackgroundProfileFetch();
    dispatch({ type: type.LOADING });

    const response = await AuthService.login(loginEmail, credentials.password);

    if (!isCurrentAuthSession(sessionId)) {
      return null;
    }

    const loginToken = response.token;
    localStorage.setItem('token', loginToken);

    const profileResponse = await AuthService.getuserprofile(
      loginAbortController.signal,
      loginToken
    );

    if (!isCurrentAuthSession(sessionId)) {
      return null;
    }

    if (profileResponse.status !== 200) {
      throw new Error('Failed to load profile after login');
    }

    const profile = profileResponse.data;

    if (!profileMatchesLoginEmail(profile, loginEmail)) {
      localStorage.removeItem('token');
      dispatch({ type: type.LOGOUT });
      throw new Error('Login profile mismatch');
    }

    dispatch({
      type: type.UPDATE_USER,
      payload: profile,
    });

    return profile;
  } catch (error) {
    if (isRequestCancelled(error)) {
      throw error;
    }
    dispatch({
      type: type.ERROR,
      payload: error.response ? error.response.data : { message: 'Login failed' },
    });
    throw error;
  }
};


export const getProfile = () => (dispatch, getState) => {
  const sessionId = getAuthSession();
  const tokenAtRequestTime = localStorage.getItem('token');
  if (!tokenAtRequestTime) {
    dispatch({ type: type.LOGOUT });
    return;
  }

  abortBackgroundProfileFetch();
  const profileSignal = backgroundProfileAbortController.signal;

  dispatch({ type: type.LOADING });

  AuthService.getuserprofile(profileSignal, tokenAtRequestTime)
      .then((resp) => {
          const tokenNow = localStorage.getItem('token');
          const requestUserId = getUserIdFromToken(tokenAtRequestTime);
          const currentUserId = getUserIdFromToken(tokenNow);
          const switchedUser = Boolean(
            requestUserId &&
            currentUserId &&
            String(requestUserId) !== String(currentUserId)
          );

          if (!isCurrentAuthSession(sessionId) || switchedUser) {
            // Session changed while this request was in flight (e.g. logout/login
            // in another tab). Re-run against whatever is current instead of
            // leaving `loading` stuck forever.
            // A same-user JWT refresh (X-Refresh-Token) must still apply this body.
            dispatch(getProfile());
            return;
          }
          const profile = resp.data;
          if (profile && typeof profile === 'object' && profile._id) {
              dispatch({
                  type: type.UPDATE_USER,
                  payload: profile,
              });
          } else if (resp.status === 304 && getState().user.profile?._id) {
              dispatch({
                  type: type.UPDATE_USER,
                  payload: getState().user.profile,
              });
          } else {
              dispatch({
                  type: type.ERROR,
                  payload: resp.data?.exception,
              });
          }
      })
      .catch((error) => {
          if (isRequestCancelled(error)) {
            return;
          }
          if (error.response && (error.response.status === 400 || error.response.status === 401)) {
              localStorage.removeItem('token');
              dispatch({ type: type.LOGOUT });
          } else {
              dispatch({
                  type: type.ERROR,
                  payload: error.message,
              });
          }
      });
};

export const logoutUser = () => async (dispatch) => {
  bumpAuthSession();
  abortBackgroundProfileFetch();
  localStorage.removeItem('token');
  clearPersistedUserState();
  dispatch({ type: type.LOGOUT });
  dispatch({ type: type.CLEAR_SEMESTER });

  try {
    const { persistor } = await import('../index');
    await persistor.purge();
  } catch {
    // Non-fatal if persist purge fails
  }

  if (window.location.pathname !== '/') {
    window.location.assign('/');
  }
};

/**
 * Refreshes the user's profile to pick up any permission changes.
 * Does not flip the global loading flag — that unmounts the current page
 * and lets a stale list fetch overwrite the permission that was just saved.
 */
export const refreshUserPermissions = () => (dispatch) => {
  const sessionId = getAuthSession();
  const tokenAtRequestTime = localStorage.getItem('token');
  if (!tokenAtRequestTime) return;

  AuthService.getuserprofile(undefined, tokenAtRequestTime)
    .then((resp) => {
      if (!isCurrentAuthSession(sessionId)) return;

      const tokenNow = localStorage.getItem('token');
      const requestUserId = getUserIdFromToken(tokenAtRequestTime);
      const currentUserId = getUserIdFromToken(tokenNow);
      if (
        requestUserId &&
        currentUserId &&
        String(requestUserId) !== String(currentUserId)
      ) {
        return;
      }

      const profile = resp.data;
      if (!profile || typeof profile !== 'object' || !profile._id) return;

      dispatch({
        type: type.UPDATE_USER,
        payload: profile,
      });
    })
    .catch((error) => {
      console.error('Error refreshing user permissions:', error);
    });
};

export const updateUser = (username) => (dispatch) => {
  dispatch(action.updateUser(username))
};

export const updateError = (error) => (dispatch) => {
  dispatch(action.updateError(error))
};


export const registerUser = (userData) => async (dispatch) => {
  const sessionId = bumpAuthSession();

  try {
    abortBackgroundProfileFetch();
    const response = await AuthService.register(userData);

    if (response.data.status !== "OK") {
      const errorMessage = response.data.exception || response.data.message || 'Registration failed';
      dispatch({
        type: type.ERROR,
        payload: errorMessage
      });
      throw new Error(errorMessage);
    }

    const token = response.token;
    if (!isCurrentAuthSession(sessionId)) {
      return null;
    }

    const profileResponse = await AuthService.getuserprofile(undefined, token);
    if (!isCurrentAuthSession(sessionId)) {
      return null;
    }

    const profile = profileResponse.data;
    const userIdFromToken = getUserIdFromToken(token);

    if (
      userIdFromToken &&
      profile._id &&
      String(profile._id) !== String(userIdFromToken)
    ) {
      throw new Error('Registration profile mismatch');
    }

    const resolvedProfile = {
      ...profile,
      _id: userIdFromToken || profile._id,
      token,
    };

    dispatch({
      type: type.UPDATE_USER,
      payload: resolvedProfile,
    });
    dispatch(action.updateError(null));
    return resolvedProfile;
  } catch (error) {
    let errorMessage = 'Registration failed';

    if (error.response?.data?.exception) {
      errorMessage = error.response.data.exception;
    } else if (error.response?.data?.error) {
      errorMessage = error.response.data.error;
    } else if (error.message) {
      errorMessage = error.message;
    }

    throw new Error(errorMessage);
  }
};



export const submitForApproval = (userData) => (dispatch) => {
  AuthService.submitForApproval(userData)
    .then((resp) => {
      if (resp.data.status === "OK") {
        dispatch(action.updateError(null));
      } else {
        dispatch(action.updateError(resp.data.exception));
      }
    })
    .catch((error) => {
      dispatch(action.updateError(error.message));
    });
};
